import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'
import slice from 'lodash/fp/slice'
import filter from 'lodash/fp/filter'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'
import last from 'lodash/last'

import urlFor from 'shared/tools/url-helper'

import prepareComponent from 'client/shared/decorators/prepare-component'
import { getShelfById } from 'client/bookmate/reducers/shelves-reducer'
import { load, loadPosts } from 'client/bookmate/reducers/shelf-reducer'
import { getPosts } from 'client/bookmate/reducers/posts-reducer'

import Meta from 'client/shared/blocks/meta'
import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import MetaTwinBox from 'client/shared/boxes/meta-twin-box'
import MetaDeeplink from 'client/shared/blocks/meta-deeplink'
import { FullShelfProps } from 'client/shared/types/shelf'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { PostProps } from 'client/shared/types/post'
import { Route } from 'client/shared/types/react-router'
import { State } from 'shared/types/redux'

const dupPages = ['followers', 'posts', 'posts/:postUuid', 'books']

const EDITORIAL_CREATOR = [
  987007,
  2164470,
  2736968,
  3578737,
  3596164,
  3679146,
  3679548,
  4012263,
  4280013,
  5486940,
  6657684,
  7038328,
  8681256,
  9042231,
]

const getBookTitlesStr = compose(
  join(', '),
  map('resource.title'),
  filter(post => /book$/.test(post.resource_type)),
  slice(0, 20),
)

type Props = {
  shelf: FullShelfProps
  app: AppState
  shelfPosts: PostProps[]
  routes?: Route[]
} & withI18nProps

class ShelfPage extends Component<Props> {
  isSeries() {
    return this.props.shelf.type === 'series'
  }

  render(): JSX.Element | null {
    if (isEmpty(this.props.shelf)) return null

    return (
      <>
        {this.renderMeta()}
        {this.props.children}
      </>
    )
  }

  renderMeta(): JSX.Element {
    const {
      app: { host },
      i18n,
      shelf: {
        title,
        creator,
        uuid,
        last_document_at,
        language,
        annotation,
        followers_count,
      },
      shelfPosts,
      routes,
    } = this.props

    let headTitle
    if (this.isSeries()) {
      headTitle = i18n._('shelf.head_title_series', { series: title })
    } else {
      headTitle = i18n._('shelf.head_title', {
        title,
        name: creator.name || creator.login,
      })
    }
    const shelfRoute = urlFor.shelf(uuid)
    const ogUrl = `https://${host}${shelfRoute}`
    const description = annotation || getBookTitlesStr(shelfPosts)
    const isDupPage = dupPages.includes(last(routes).path)

    let MetaTwinBoxProps = {}
    if (isDupPage) {
      MetaTwinBoxProps = {
        customPathname: shelfRoute,
        robotsParams: {
          name: 'robots',
          content: 'noindex,nofollow',
        },
      }
    }

    const MIN_FOLLOWERS_COUNT = 50
    const isEditorialShelf = EDITORIAL_CREATOR.includes(creator.id)

    return (
      <>
        <MetaTitle title={headTitle} disableSuffix />
        {description && <MetaDescription description={description} />}
        {language && <MetaTwinBox language={language} {...MetaTwinBoxProps} />}
        <Meta content="summary_large_image" property="twitter:card" />
        <MetaDeeplink target="shelf" uuid={uuid} />
        <Meta
          content={urlFor.canvasShelf({
            shelfId: uuid,
            mode: 'twitter',
            timestamp: last_document_at,
          })}
          property="twitter:image"
        />
        <Meta
          content={urlFor.canvasShelf({
            shelfId: uuid,
            mode: 'facebook',
            timestamp: last_document_at,
          })}
          property="og:image"
        />
        <Meta content="website" property="og:type" />
        <Meta content={ogUrl} property="og:url" />
        {followers_count <= MIN_FOLLOWERS_COUNT && !isEditorialShelf && (
          <Meta name="robots" content="noindex,nofollow" />
        )}
      </>
    )
  }
}

function shouldLoadPosts(pathname: string): boolean {
  const regexes = [
    /\/bookshelves\/.+?\/posts(\/|$)/, // paths handled by ShelfPostsBox or PostFormBox
  ]
  const willChildComponentLoadPosts = regexes.some(regex =>
    regex.test(pathname),
  )
  return !willChildComponentLoadPosts
}

const prepareWrapper = prepareComponent(
  // eslint-disable-next-line func-names
  async function ({ store, params, location }) {
    const { uuid } = params
    const { dispatch } = store
    const actions = [dispatch(load(uuid))]
    if (shouldLoadPosts(location.pathname)) {
      actions.push(dispatch(loadPosts({ uuid, p: 1, pp: 20 })))
    }
    await Promise.all(actions)
  },
  {
    updateParams: 'uuid',
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  app: state.app,
  currentUser: state.currentUser,
  shelf: getShelfById(state, state.shelf.uuid),
  shelfPosts: getPosts(state.shelf.posts, state.entities),
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withI18n({ update: true }),
)

export default wrappers(ShelfPage)
